/**
 * 24-hour timeline strip for the Activity page. Shows each button
 * press as a tick at its time-of-day position; the strip's
 * background is split into "evening" (before wake / after bedtime)
 * and "daytime" (wake to bedtime) so a caregiver can spot at a
 * glance whether presses are clustering at unusual hours.
 *
 * When the account has multiple buttons each device gets its own
 * colour (stable, by claim order) so the caregiver can see at a
 * glance whether presses are coming from the kitchen, the bedside,
 * etc. A legend under the strip shows room labels with per-device
 * counts for the day.
 *
 * Wake / bedtime mirror the spoken summary's WAKE_HOUR /
 * BEDTIME_HOUR — same values the elder hears nudges against.
 */

import { formatInTimeZone } from 'date-fns-tz';

import { BEDTIME_HOUR, WAKE_HOUR } from '@/lib/voices';
import type { ButtonPressWithId } from '@/hooks/useButtonPresses';
import type { DeviceWithId } from '@/hooks/useDevices';

interface Props {
  presses: ButtonPressWithId[];
  devices: DeviceWithId[];
  tz: string;
  wakeHour?: number;
  bedtimeHour?: number;
}

// Per-device tick + swatch colours, in stable order. Written as
// literal class strings so Tailwind's content scanner picks them up.
// The first slot is the brand accent so single-device accounts look
// the same as before; subsequent slots are distinct hues at the same
// weight. Capped at MAX_BUTTONS_PER_ACCOUNT (5) but a `fallback` is
// used for any press whose device was removed.
const PALETTE = [
  { tick: 'bg-accent-700',   swatch: 'bg-accent-700' },
  { tick: 'bg-teal-700',     swatch: 'bg-teal-700' },
  { tick: 'bg-violet-700',   swatch: 'bg-violet-700' },
  { tick: 'bg-rose-700',     swatch: 'bg-rose-700' },
  { tick: 'bg-emerald-700',  swatch: 'bg-emerald-700' },
];
const FALLBACK = { tick: 'bg-ink-muted', swatch: 'bg-ink-muted' };

export function ActivityTimeline({ presses, devices, tz, wakeHour, bedtimeHour }: Props) {
  const wake = clampHour(wakeHour ?? WAKE_HOUR.default);
  const bed = clampHour(bedtimeHour ?? BEDTIME_HOUR.default);
  const wakePct = (wake / 24) * 100;
  const bedPct = (bed / 24) * 100;

  // Sort devices by claimedAt for a stable colour mapping that
  // doesn't shuffle as devices are renamed. Fall back to id when
  // claimedAt is absent (legacy / unclaimed-but-listed).
  const orderedDevices = [...devices].sort((a, b) => {
    const ta = a.claimedAt?.toMillis() ?? Number.MAX_SAFE_INTEGER;
    const tb = b.claimedAt?.toMillis() ?? Number.MAX_SAFE_INTEGER;
    if (ta !== tb) return ta - tb;
    return a.id.localeCompare(b.id);
  });
  const colorFor = (deviceId: string) => {
    const idx = orderedDevices.findIndex((d) => d.id === deviceId);
    if (idx === -1) return FALLBACK;
    return PALETTE[idx % PALETTE.length] ?? FALLBACK;
  };

  const countsByDevice = new Map<string, number>();
  for (const p of presses) {
    countsByDevice.set(p.deviceId, (countsByDevice.get(p.deviceId) ?? 0) + 1);
  }

  return (
    <div className="mb-4">
      <div
        className="relative h-10 w-full overflow-hidden rounded-md border border-paper-border bg-paper-alt"
        role="img"
        aria-label={`Timeline of ${presses.length} button press${presses.length === 1 ? '' : 'es'}`}
      >
        {/* Daytime band (wake → bedtime). The slate background is the
           evening default; this single warm strip overlays the
           daytime portion. */}
        <div
          className="absolute inset-y-0 bg-amber-50"
          style={{ left: `${wakePct}%`, width: `${bedPct - wakePct}%` }}
        />
        {/* Hour gridlines at 0, 6, 12, 18 — soft references without
           crowding. */}
        {[6, 12, 18].map((h) => (
          <div
            key={h}
            className="absolute inset-y-0 w-px bg-paper-border"
            style={{ left: `${(h / 24) * 100}%` }}
            aria-hidden
          />
        ))}
        {/* Press marks. Stacking by time means later presses sit on
           top of earlier ones at the same position; for a 24h day at
           ~600px width, each pixel ≈ 2.4 minutes — overlap is fine. */}
        {presses.map((p) => {
          const minutes = minutesInTz(p.pressedAt.toDate(), tz);
          const c = colorFor(p.deviceId);
          return (
            <div
              key={p.id}
              className={`absolute top-1 bottom-1 w-0.5 -translate-x-px rounded-sm ${c.tick}`}
              style={{ left: `${(minutes / 1440) * 100}%` }}
              title={`${formatInTimeZone(p.pressedAt.toDate(), tz, 'HH:mm')} · ${p.roomLabel || 'Device'}`}
            />
          );
        })}
      </div>
      {/* Hour labels under the strip. */}
      <div className="relative mt-1 h-3 text-[10px] text-ink-muted">
        {[0, 6, 12, 18, 24].map((h) => (
          <span
            key={h}
            className="absolute -translate-x-1/2"
            style={{ left: `${(h / 24) * 100}%` }}
          >
            {h === 24 ? '24:00' : `${h.toString().padStart(2, '0')}:00`}
          </span>
        ))}
      </div>
      {/* Device legend. Only worth showing when there are 2+ devices —
         a single-device account doesn't need a key. Always lists every
         claimed device (including those with 0 presses today) so the
         caregiver can spot a button that's gone unused. */}
      {orderedDevices.length >= 2 && (
        <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-soft">
          {orderedDevices.map((d) => {
            const c = colorFor(d.id);
            const count = countsByDevice.get(d.id) ?? 0;
            return (
              <li key={d.id} className="flex items-center gap-1.5">
                <span
                  className={`inline-block h-2.5 w-2.5 rounded-sm ${c.swatch}`}
                  aria-hidden
                />
                <span>
                  {d.roomLabel || 'Device'}{' '}
                  <span className="text-ink-muted">({count})</span>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function clampHour(h: number): number {
  if (Number.isNaN(h)) return 0;
  if (h < 0) return 0;
  if (h > 24) return 24;
  return h;
}

function minutesInTz(d: Date, tz: string): number {
  const hh = Number(formatInTimeZone(d, tz, 'HH'));
  const mm = Number(formatInTimeZone(d, tz, 'mm'));
  return hh * 60 + mm;
}
